import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Drawer, Tabs, Tab, BarLoader, useToast, ComponentLibraryLoader } from '@unite-us/app-components';
import { datadogRum } from '@datadog/browser-rum';
import { fetchGroupContact } from 'actions/Contact/Group';
import { env } from 'src/config/env';
import {
  hbh1710ClientsDrawerLatestActivity,
  hbh1713ClientsDrawerDocuments,
  scrn7ScreeningsCompletion,
  crtb1121HideFieldInForm,
  scrn468ScreeningTimeTracking,
  hbh1025ShowEligibilityAssessmentTab,
  scrn942ScreeningsResultsUpdates,
} from 'common/utils/FeatureFlags/flags';
import { dollarAmount } from 'src/common/display/Money/DollarAmount';
import { ASSESSMENTS } from 'src/common/utils/EventTracker/utils/eventConstants';
import FacesheetOverview from 'src/pages/facesheet/_id/FacesheetOverview';
import FacesheetProfile from 'src/pages/facesheet/_id/profile/FacesheetProfile';
import FacesheetCases from 'src/pages/facesheet/_id/cases/FacesheetCases';
import FacesheetUploads from 'src/pages/facesheet/_id/uploads/Uploads';
import { browserHistory } from 'src/common/utils/browserHistory';
import { getAuthToken } from 'src/api/config';
import * as apiHooks from 'src/api/APIHooks';
import getClientsDetailLink from 'src/common/utils/getClientsDetailLink';
import { getContactFromState } from 'src/components/Contacts/utils';
import { isCaseManager } from 'src/components/User/utils';
import {
  MethodsOfContact,
  VoiceMailOk,
} from 'src/components/Facesheet/Profile/ContactRequirements/components';
import StatusSelect from 'src/components/StatusSelect';
import {
  generateClientData,
  getDobData,
  formatAddress,
  formatHouseholdCount,
  filterMilitaryInfo,
  formatDemographicInfo,
} from 'src/components/ClientSummary/utils';
import findPrimaryOrFirst from 'src/components/Contacts/utils/findPrimaryOrFirst';
import BackButton from 'src/common/buttons/BackButton';
import { RISK_LEVELS } from 'src/components/Screenings/constants';
import showRiskDisplayOption from 'src/components/Screenings/Detail/utils/showRiskDisplayOption';
import callOrLog from 'src/common/utils/callOrLog';
import fetchInsurances from 'src/api/core/Insurances/fetchInsurances';
import { useFindAllPlan } from 'src/api/core/Plans/planHooks';
import { PortalizationProvider } from '@unite-us/app-client-profile';
import DrawerHeader from './DrawerHeader';

const DrawerFacesheetScreenings = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-screenings'),
  component: 'DrawerFacesheetScreenings',
  source: 'app-screenings',
  loadingApp: 'web-app',
  datadogRum,
});

const ClientsDrawer = ({
  open,
  onClose,
  context,
  contact,
  groupId,
  fetchGroupContact: fetchGroupContactAction,
  hbh1710ClientsDrawerLatestActivityEnabled,
  hbh1713ClientsDrawerDocumentsEnabled,
  scrn7ScreeningsCompletionEnabled,
  hideCloseButton,
  DrawerComponent,
  employee,
  provider,
  stateContact,
  enums,
  crtb1121HideFieldInFormEnabled,
  scrn468ScreeningTimeTrackingEnabled,
  hbh1025ShowEligibilityAssessmentEnabled,
  scrn942ScreeningsResultsUpdatesEnabled,
}) => {
  const [tabValue, setTabValue] = useState('overview');
  const [fullContact, setFullContact] = useState(null);
  const [isFetchingFull, setIsFetchingFull] = useState(false);
  const drawerRef = useRef(null);
  const { addToast } = useToast();
  // Function to reset drawer scroll position
  const resetDrawerScroll = () => {
    // Use a short delay to ensure DOM is ready after drawer content changes
    setTimeout(() => {
      // Target dialog containers (most effective based on testing)
      const dialogElements = document.querySelectorAll('[role="dialog"]');

      dialogElements.forEach((dialog) => {
        // Reset scrollable parents and children of dialog elements
        const allElements = [dialog, ...dialog.querySelectorAll('*')];

        allElements.forEach((element) => {
          if (element.scrollHeight > element.clientHeight && element.scrollTop > 0) {
            // eslint-disable-next-line no-param-reassign
            element.scrollTop = 0;
          }
        });
      });
    }, 100);
  };

  const tabsConfig = {
    overview: {
      label: 'Overview',
      show: true,
    },
    profile: {
      label: 'Profile',
      show: true,
    },
    ...(hbh1710ClientsDrawerLatestActivityEnabled && {
      latestActivity: {
        label: 'Latest Activity',
        show: true,
      },
    }),
    screenings: {
      label: 'Screenings',
      show: scrn7ScreeningsCompletionEnabled,
    },
    uploads: {
      label: 'Uploads',
      show: hbh1713ClientsDrawerDocumentsEnabled,
    },
  };
  // Reset tab to overview only when contact ID changes
  useEffect(() => {
    if (contact?.id) {
      setTabValue('overview');
      resetDrawerScroll();
    }
  }, [contact?.id]);

  // Fetch full contact data
  useEffect(() => {
    let mounted = true;

    if (!contact?.id) {
      return () => {
        mounted = false;
      };
    }

    setIsFetchingFull(true);
    fetchGroupContactAction(groupId, contact.id)
      .then((res) => {
        if (mounted) {
          setFullContact(res.data.data);
        }
      })
      .finally(() => {
        if (mounted) setIsFetchingFull(false);
      });

    return () => {
      mounted = false;
    };
  }, [groupId, contact?.id, fetchGroupContactAction]);

  // Reset scroll when drawer opens
  useEffect(() => {
    if (open) {
      resetDrawerScroll();
    }
  }, [open]);

  const handleTabChange = (value) => {
    setTabValue(value);
    // Reset scroll when tab changes
    resetDrawerScroll();
  };
  const shouldPortalizeDialogs = !DrawerComponent;

  function renderTabComponent(Component, tabName, additionalProps = {}) {
    const baseProps = {
      key: `${tabName}-${contact?.id}`,
      contact: fullContact || contact,
      shouldPortalizeDialogs,
    };

    return <Component {...baseProps} {...additionalProps} />;
  }

  function renderOverviewTab() {
    return (
      <FacesheetOverview
        key={`overview-${contact?.id}`}
        id={contact?.id}
        isClientsDrawer
        contact={fullContact}
        shouldPortalizeDialogs={shouldPortalizeDialogs}
        visibilityConfig={{
          overview: {
            showTab: true,
            showCareCoordinator: true,
            showCareTeam: true,
            showFamilyMembers: true,
            showOpenCases: true,
            showNotes: true,
            showMessages: true,
          },
        }}
        getClientRelationship={() => { }}
      />
    );
  }

  function renderProfileTab() {
    return renderTabComponent(FacesheetProfile, 'profile', {
      ...context,
      params: { id: contact?.id },
      isClientsDrawer: true,
      setHeaderContact: setFullContact,
    });
  }

  function renderLatestActivityTab() {
    return (
      hbh1710ClientsDrawerLatestActivityEnabled ?
        (
          <FacesheetCases
            key={`cases-${contact?.id}`}
            id={contact?.id}
            isClientsDrawer
            contact={contact}
            shouldPortalizeDialogs={shouldPortalizeDialogs}
          />
        ) : (<div className="container" key={`coming-soon-${contact?.id}`}>Coming Soon</div>)
    );
  }

  function renderScreeningsTab() {
    const formFilter = '';
    const appStateObj = {
      enums: enums || {},
      groupId,
      isCaseManagerUser: isCaseManager(employee, groupId),
      providerId: provider?.id,
      currentEmployee: employee,
    };
    if (!employee || !provider) {
      return (
        <div className="flex justify-center pt-6 pb-6 w-full">
          <BarLoader className="h-38px" size="full" />
        </div>
      );
    }
    return (
      <DrawerFacesheetScreenings
        key={`screenings-${contact?.id}`}
        source="web_app"
        hooks={{ ...apiHooks, useFindAllPlan }}
        callbacks={{
          notify: {
            error: (message) => addToast({ message, type: 'error' }),
            success: (message) => addToast({ message, type: 'success' }),
            warn: (message) => addToast({ message, type: 'warning' }),
          },
          goBack: () => {
            browserHistory.goBack();
          },
          replace: (path) => {
            browserHistory.replace(path);
          },
          pushInHistory: (url) => {
            browserHistory.push(url);
          },
          getClientsDetailLink,
        }}
        hasFilters={formFilter !== ''}
        contact={stateContact || contact}
        appSettings={{
          env: {
            getAuthToken,
            employee,
            employeeId: employee?.id,
            providerId: provider?.id,
            provider,
            coreUrl: env.CORE_API,
          },
          flags: {
            crtb1121HideFieldInFormEnabled,
            scrn468ScreeningTimeTrackingEnabled,
            hbh1025ShowEligibilityAssessmentEnabled,
            scrn942ScreeningsResultsUpdatesEnabled,
          },
        }}
        appState={appStateObj}
        components={{
          MethodsOfContact: MethodsOfContact || (() => <div>MethodsOfContact Missing</div>),
          VoiceMailOk: VoiceMailOk || (() => <div>VoiceMailOk Missing</div>),
          StatusSelect: StatusSelect || (() => <div>StatusSelect Missing</div>),
          BackButton: BackButton || (() => <div>BackButton Missing</div>),
        }}
        utils={{
          fetchInsurances,
          generateClientData,
          getDobData,
          formatAddress,
          formatHouseholdCount,
          filterMilitaryInfo,
          formatDemographicInfo,
          findPrimaryOrFirst,
          dollarAmount,
          showRiskDisplayOption,
          callOrLog,
        }}
        constants={{ RISK_LEVELS, ASSESSMENTS }}
      />
    );
  }

  function renderUploadsTab() {
    return renderTabComponent(FacesheetUploads, 'uploads', {
      params: { id: contact?.id },
      searchedTerm: '',
      hideUploadButton: true,
    });
  }

  const setTabContent = () => {
    const tabContentMap = {
      overview: renderOverviewTab,
      profile: renderProfileTab,
      latestActivity: renderLatestActivityTab,
      screenings: renderScreeningsTab,
      uploads: renderUploadsTab,
    };
    const renderFn = tabContentMap[tabValue] || (() => null);
    return renderFn();
  };

  const DrawerContent = (
    <PortalizationProvider shouldPortalizeDialogs={shouldPortalizeDialogs}>
      <div key={`drawer-content-${contact?.id}-${tabValue}`}>
        <div className="container px-4 pt-4 border-b border-solid border-light-border-blue">
          <DrawerHeader
            contact={contact}
            fullContact={fullContact}
            isFetchingFull={isFetchingFull}
            groupId={groupId}
            hbh1710ClientsDrawerLatestActivity={hbh1710ClientsDrawerLatestActivity}
          />
          <Tabs
            id="client-drawer-tabs"
            className="facesheet-tabs"
            onChange={handleTabChange}
            value={tabValue}
          >
            {Object.keys(tabsConfig).map((tab) => {
              const { label, show } = tabsConfig[tab];
              return show ? (
                <Tab
                  id={`clientsDrawer-${tab}-tab`}
                  label={label}
                  value={tab}
                  className={
                    `px-4 py-2 border-b-2 border-solid border-t-0 bg-white hover:bg-light-fill-blue text-brand-blue 
                    ${tabValue === tab ?
                      'border-action-blue font-semibold' :
                      'border-transparent'
                    }`
                  }
                  key={tab}
                />
              ) : null;
            })}
          </Tabs>
        </div>
        <div className="bg-medium-fill-grey pt-4 px-4 min-h-screen mb-4" key={`content-${contact?.id}-${tabValue}`}>
          {setTabContent()}
        </div>
      </div>
    </PortalizationProvider>
  );

  const DrawerContainer = DrawerComponent ? (
    <DrawerComponent open={open} setOpen={onClose} ref={drawerRef}>
      {DrawerContent}
    </DrawerComponent>
  ) : (
    <div ref={drawerRef}>
      <Drawer
        open={open}
        secondary
        topOffset="60px"
        width="500px"
        onClose={onClose}
        hideCloseButton={hideCloseButton}
        iconColor="action-blue"
        className="border border-solid border-light-border-blue"
      >
        {DrawerContent}
      </Drawer>
    </div>
  );

  return DrawerContainer;
};

ClientsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.object,
  contact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    last_name_first_name: PropTypes.string.isRequired,
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }).isRequired,
  hbh1710ClientsDrawerLatestActivity: PropTypes.bool,
  hbh1713ClientsDrawerDocumentsEnabled: PropTypes.bool,
  scrn7ScreeningsCompletionEnabled: PropTypes.bool,
  groupId: PropTypes.string.isRequired,
  fetchGroupContact: PropTypes.func.isRequired,
  hideCloseButton: PropTypes.bool,
  DrawerComponent: PropTypes.elementType,
  employee: PropTypes.object,
  provider: PropTypes.object,
  stateContact: PropTypes.object,
  enums: PropTypes.object,
};
function mapStateToProps(state, ownProps) {
  const { session } = state;
  const currentProvider = state.globalState?.currentProvider?.group;
  const currentEmployee = state.globalState?.currentEmployee;
  const contactId = ownProps.contact?.id || '';
  const stateContact = getContactFromState(state, contactId);
  const enums = get(state, 'session.enums');
  const crtb1121HideFieldInFormEnabled = crtb1121HideFieldInForm(state);
  const scrn468ScreeningTimeTrackingEnabled = scrn468ScreeningTimeTracking(state);
  const hbh1025ShowEligibilityAssessmentEnabled = hbh1025ShowEligibilityAssessmentTab(state);
  const scrn942ScreeningsResultsUpdatesEnabled = scrn942ScreeningsResultsUpdates(state);
  return {
    hbh1710ClientsDrawerLatestActivityEnabled: hbh1710ClientsDrawerLatestActivity(state),
    hbh1713ClientsDrawerDocumentsEnabled: hbh1713ClientsDrawerDocuments(state),
    scrn7ScreeningsCompletionEnabled: scrn7ScreeningsCompletion(state),
    groupId: session?.groupId,
    provider: currentProvider,
    employee: currentEmployee,
    stateContact,
    enums,
    crtb1121HideFieldInFormEnabled,
    scrn468ScreeningTimeTrackingEnabled,
    hbh1025ShowEligibilityAssessmentEnabled,
    scrn942ScreeningsResultsUpdatesEnabled,
  };
}

export default connect(
  mapStateToProps,
  { fetchGroupContact },
)(ClientsDrawer);
